import "preact/debug";
import { useState, useContext, useEffect } from "preact/hooks";
import { createContext, render } from "preact";
import styles from "./news-listing.module.css";
import articleStyles from "./news-article.module.css";
import ReactPaginate from "react-paginate";
import { ReactPaginateExtended } from "./reactPaginateExtended";
import { v4 as uuidv4 } from 'uuid';

export const FilterState = createContext(null);

/**
 *
 * @param {Array} filters - Array of filters
 * @param {Function} resetFilters - Callback function to App to trigger the global resetting filters
 * @returns JSX
 */
function SideMenu({ filters, resetFilters }) {
  return (
    <div class="">
      <div class="d-flex flex-wrap align-items-center justify-content-between mb-4">
        <header class={`${styles["header"]} font-broad-bold text-uppercase`}>
          <span class={`${styles["header__icon"]} material-symbols-outlined`}>
            tune
          </span>
          Filters
        </header>
        <button
          type="reset"
          class={`${styles["reset-button"]} font-bold text-uppercase`}
          onClick={() => resetFilters()}
        >
          <span
            class={`${styles["reset-button__icon"]} material-symbols-outlined autorenew`}
          ></span>
          Reset
        </button>
      </div>
      <nav aria-label="Side">
        {filters.map((filter) => (
          <Filter filter={filter} />
        ))}
      </nav>
    </div>
  );
}

/**
 *
 * @param {Boolean} active - To set if the icon should display its active state
 * @returns SVG icon
 */
function Chevron({ active }) {
  const id = uuidv4();
  return (
    <svg
      aria-label="chevron"
      role="img"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={
        active
          ? `${styles["filter-category__chevron"]} ${styles["filter-category__chevron--active"]}`
          : styles["filter-category__chevron"]
      }
    >
      <g>
        <mask
          id={id}
          style="mask-type:alpha"
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="24"
          height="24"
        >
          <rect width="24" height="24" />
        </mask>
        <g mask={`url(#${id})`}>
          <path
            d="M12 15.4L6 9.4L7.4 8L12 12.6L16.6 8L18 9.4L12 15.4Z"
          />
        </g>
      </g>
    </svg>
  );
}

/**
 *
 * @param {Object} Filter - The object representing this Filter, contains a list of options that can be checked
 * @returns JSX
 */
function Filter({ filter }) {
  const [active, setActive] = useState(false);
  const { setFilterState, selectedFilters } = useContext(FilterState);

  useEffect(() => {
    const hasKeys = !!Object.keys(selectedFilters).length;
    if (!hasKeys) {
      setActive(false);
    }
  }, [selectedFilters]);

  return (
    <div class="">
      <button
        type="button"
        onClick={() => setActive(!active)}
        class={`${styles["filter-category"]} font-bold`}
      >
        {filter.name}
        <Chevron active={active} />
      </button>
      <hr class={`${styles["filter__hr"]} opacity-25`} />
      
        <div class={`${active ? "" : "visually-hidden"}  mb-4`}>
          {filter.options.map((option) => (
            <label
              htmlFor={`checkbox-${option.id}`}
              key={option.id}
              class={`${styles["filter-option"]} font-light mb-1`}
            >
              <input
                type="checkbox"
                name="checkboxes"
                id={`checkbox-${option.id}`}
                value={option.value}
                onInput={(e) =>
                  setFilterState(filter.id, option.id, e.target.checked)
                }
                class={`${styles["filter-option__input"]} visually-hidden`}
              />
              <span class={styles["filter-option__input-proxy"]}></span>
              {option.name}
            </label>
          ))}
        </div>
      
    </div>
  );
}

/**
 *
 * @param {Object} featured - The featured news article
 * @returns JSX
 */
function FeaturedItem({ featured }) {
  return (
    <article
      class={`${articleStyles["article"]} ${articleStyles["article--feature"]} ${styles["mb-33"]} `}
    >
      <div class={`${articleStyles["article__image"]}`}>
        <corner-element
          bottom-right
          positive-color="var(--theme-news-listing-feature-corner-positive)"
          negative-color="var(--theme-news-listing-feature-corner-negative)"
        >
          <img
            src={featured.src}
            alt={featured.alt}
            loading="lazy"
            data-object-fit="cover"
            class="news-listing__image"
          />
        </corner-element>
      </div>
      <div class={articleStyles["article__content"]}>
        <a href={featured.url} class={articleStyles["article__link"]}>
          <h2
            class={`${articleStyles["article__heading"]} font-broad-bold mb-3`}
          >
            {featured.name}
          </h2>
        </a>
        <p class={`${articleStyles["article__description"]}`}>
          {featured.excerpt}
        </p>
        <div class="button __outline mt-3">Read more</div>
      </div>
    </article>
  );
}

/**
 *
 * @param {Object} item - An article containing image, name and url
 * @returns JSX
 */
function Item({ item }) {
  return (
    <article
      class={`${articleStyles["article"]} ${articleStyles["article--hover-effect"]}`}
    >
      <div class={`${articleStyles["article__image-wrapper"]} mb-4`}>
        <img
          src={item.src}
          alt={item.alt}
          loading="lazy"
          data-object-fit="cover"
          class={`${articleStyles["article__image"]}`}
        />
      </div>
      <div>
        <a href={item.url} class={articleStyles["article__link"]}>
          <h2
            class={`${articleStyles["article__heading"]} font-broad-bold mb-4`}
          >
            {item.name}
          </h2>
        </a>
        <div
          class={`${articleStyles["article__button"]} font-bold text-uppercase`}
        >
          <span class="material-symbols-outlined">keyboard_arrow_right</span>
          Read More
        </div>
      </div>
    </article>
  );
}

function Pagination({ handlePageClick, pageCount }) {
  try {
    return (
      <ReactPaginateExtended
        breakLabel="..."
        nextLabel="Next"
        onPageChange={handlePageClick}
        pageRangeDisplayed={4}
        pageCount={pageCount}
        previousLabel="Prev"
        renderOnZeroPageCount={null}
        previousLinkClassName={styles["prev-page"]}
        nextLinkClassName={styles["next-page"]}
        pageLinkClassName={styles["page"]}
        activeLinkClassName={styles["page-active"]}
        containerClassName={styles.pagination}
      />
    );
  } catch (error) {
    console.error("Pagination failed and returned default instead");
    return (
      <ReactPaginate
        breakLabel="..."
        nextLabel="Next"
        onPageChange={handlePageClick}
        pageRangeDisplayed={4}
        pageCount={pageCount}
        previousLabel="Prev"
        renderOnZeroPageCount={null}
        previousLinkClassName={styles["prev-page"]}
        nextLinkClassName={styles["next-page"]}
        pageLinkClassName={styles["page"]}
        activeLinkClassName={styles["page-active"]}
        containerClassName={styles.pagination}
      />
    );
  }
}

/**
 *
 * @param {Array} items - The array of articles
 * @param {Object} featured - The featured article
 * @param {Array} filters - The filters that can be selected to filter the articles
 * @returns JSX
 */
function App({ items, featured, filters }) {
  const [selectedFilters, setSelectedFilters] = useState({});
  const [pageCount, setPageCount] = useState(1);
  const itemsPerPage = 2;
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [paginatedPosts, setPaginatedPosts] = useState([]);

  useEffect(() => {
    createPages(items, 0);
  }, []);

  function resetFilters() {
    setSelectedFilters({});
  }

  // When a new filter is selected scan through the posts and selected filters
  // to see if any articles have categories that match the selected filters
  // Example
  // the category key matches the location in the array of the filter that needs to match
  // e.g filters = [{..., options: [Ben, James]}, {another filter}, {another filter}]
  // article = {..., categories: {0: ["Ben"]} }
  // the article would match the first filter
  useEffect(() => {
    const newPosts = [...items];

    const filteredPost = newPosts.filter((newPost) => {
      const result = [];
      const categories = Object.keys(selectedFilters);
      for (const category of categories) {
        const keys = Object.keys(selectedFilters[category]);
        for (const key of keys) {
          if (selectedFilters[category][key] === true) {
            const postHasCategory = newPost.categories[category].includes(key);
            result.push(postHasCategory);
          }
        }
      }
      return result.every(Boolean);
    });
    setFilteredPosts(filteredPost);
  }, [selectedFilters]);

  // Create a object using the id of the filter as the key. the filtervalue will be true or false
  // This object will only become as big as the number of filters in the filter array
  function setFilterState(filterId, optionId, filterValue) {
    setSelectedFilters({
      ...selectedFilters,
      [filterId]: {
        ...selectedFilters[filterId],
        [optionId]: filterValue,
      },
    });
  }

  function createPages(newItems, newOffset) {
    const endOffset = newOffset + itemsPerPage;
    const currentItems = newItems.slice(newOffset, endOffset);
    setPaginatedPosts(currentItems);
    setPageCount(Math.ceil(newItems.length / itemsPerPage));
  }

  function handlePageClick(event) {
    const newOffset = (event.selected * itemsPerPage) % filteredPosts.length;
    createPages(filteredPosts, newOffset);
  }

  useEffect(() => {
    createPages(filteredPosts, 0);
  }, [filteredPosts]);

  return (
    <div class={styles["listing-bg"]}>
      <div class="container p-0 px-md-3">
        <div class={styles.grid}>
          <div
            class={`${styles.filter} ${styles["grid__side-bar"]} mb-3 mb-md-0`}
          >
            <div class={`container`}>
              <FilterState.Provider value={{ setFilterState, selectedFilters }}>
                <SideMenu filters={filters} resetFilters={resetFilters} />
              </FilterState.Provider>
            </div>
          </div>
          <div class={styles["grid__content"]}>
            <div class="container">
              <FeaturedItem featured={featured} />

              <div class="row mb-5">
                {paginatedPosts.map((item) => (
                  <div class="col-md-6 col-lg-4">
                    <Item item={item} />
                  </div>
                ))}
              </div>

              <Pagination
                handlePageClick={handlePageClick}
                pageCount={pageCount}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const newsNode = document.getElementById("news-listing");

if (newsNode) {
  render(
    <App
      items={globalThis.items}
      featured={globalThis.featured}
      filters={globalThis.filters}
    />,
    newsNode
  );
}
